import type { SettlementIslandBridge } from './settlement-island-bridges';
import {
  SETTLEMENT_ISLAND_ART,
  type IslandPoint,
  type IslandQuad,
} from './settlement-island-registration';

/** Authored dimensions in original basin pixels, except the explicitly named ground cells. */
export const SETTLEMENT_BRIDGE_STRUCTURE = {
  postHeight: 12,
  railHeightRatio: 0.75,
  railThickness: 1.4,
  endInsetCells: 0.23,
  maxPostSpacingCells: 1.6,
  maxPosts: 18,
} as const;

export interface SettlementBridgePost {
  base: IslandPoint;
  top: IslandPoint;
  height: number;
  /** Uniform source-to-viewport scale, also applicable to an upright sprite's width. */
  scale: number;
}

export interface SettlementBridgeRail {
  /** Vertical side face: top-start, top-end, bottom-end, bottom-start. Not a ground plane. */
  quad: IslandQuad;
}

export interface SettlementBridgeSide {
  /** Posts sorted by ground-contact Y, while rails retain primary-to-expansion order. */
  posts: SettlementBridgePost[];
  rails: SettlementBridgeRail[];
}

export interface SettlementBridgeStructurePlan {
  /** Draw before the deck. */
  far: SettlementBridgeSide;
  /** Draw after the deck. */
  near: SettlementBridgeSide;
}

function finitePoint(point: IslandPoint | undefined): point is IslandPoint {
  return !!point && Number.isFinite(point.x) && Number.isFinite(point.y);
}

function affineQuad(quad: IslandQuad): boolean {
  if (!Array.isArray(quad) || quad.length !== 4) return false;
  if (![quad[0], quad[1], quad[2], quad[3]].every(finitePoint)) return false;
  const dx = quad[1].x - quad[0].x;
  const dy = quad[1].y - quad[0].y;
  const wx = quad[3].x - quad[0].x;
  const wy = quad[3].y - quad[0].y;
  const determinant = dx * wy - dy * wx;
  return (
    Number.isFinite(determinant) &&
    determinant !== 0 &&
    close(quad[2].x - quad[1].x, wx) &&
    close(quad[2].y - quad[1].y, wy)
  );
}

function close(a: number, b: number): boolean {
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= 1e-7;
}

function groundVector(a: IslandPoint, b: IslandPoint): IslandPoint {
  const dx = (b.x - a.x) / 32;
  const dy = (b.y - a.y) / 11;
  return { x: (dx + dy) / 2, y: (dy - dx) / 2 };
}

/**
 * Presentation-only railing above an already reviewed, flat deck. These uprights are not
 * foundations and do not establish shore/water elevation or alter any ground contact.
 */
export function planSettlementBridgeStructure(
  bridge: SettlementIslandBridge,
): SettlementBridgeStructurePlan {
  const empty = (): SettlementBridgeStructurePlan => ({
    far: { posts: [], rails: [] },
    near: { posts: [], rails: [] },
  });
  if (
    !bridge ||
    !affineQuad(bridge.sourceDeckQuad) ||
    !affineQuad(bridge.deckQuad) ||
    !Array.isArray(bridge.sourceEndpoints) ||
    bridge.sourceEndpoints.length !== 2 ||
    !Array.isArray(bridge.endpoints) ||
    bridge.endpoints.length !== 2 ||
    ![
      bridge.sourceEndpoints[0],
      bridge.sourceEndpoints[1],
      bridge.endpoints[0],
      bridge.endpoints[1],
    ].every(finitePoint) ||
    !Number.isFinite(bridge.sourceWidth) ||
    bridge.sourceWidth <= 0 ||
    !Number.isFinite(bridge.width) ||
    bridge.width <= 0 ||
    bridge.groundWidthCells !== 22 / 32
  )
    return empty();
  const source = bridge.sourceDeckQuad;
  const target = bridge.deckQuad;
  const sourcePoints = [...source, ...bridge.sourceEndpoints];
  if (
    !sourcePoints.every(
      ({ x, y }) =>
        x >= 0 && y >= 0 && x <= SETTLEMENT_ISLAND_ART.width && y <= SETTLEMENT_ISLAND_ART.height,
    )
  )
    return empty();
  const scale = bridge.width / bridge.sourceWidth;
  if (!Number.isFinite(scale) || scale <= 0) return empty();
  const sourceLength = Math.hypot(
    bridge.sourceEndpoints[1].x - bridge.sourceEndpoints[0].x,
    bridge.sourceEndpoints[1].y - bridge.sourceEndpoints[0].y,
  );
  const sourceWidth = Math.hypot(source[3].x - source[0].x, source[3].y - source[0].y);
  if (
    sourceLength < 22 ||
    sourceLength > 180 ||
    !close(sourceLength, bridge.sourceLength) ||
    !close(sourceWidth, bridge.sourceWidth)
  )
    return empty();
  // Verify the exact uniform scale and translation: an affine shear cannot size upright art.
  const targetPoints = [...target, ...bridge.endpoints];
  if (
    !sourcePoints.every(
      (point, index) =>
        close(targetPoints[index]!.x - target[0].x, (point.x - source[0].x) * scale) &&
        close(targetPoints[index]!.y - target[0].y, (point.y - source[0].y) * scale),
    )
  )
    return empty();
  for (const [index, corner] of [
    [0, 0],
    [1, 1],
  ] as const) {
    const opposite = corner === 0 ? 3 : 2;
    if (
      !close((source[corner].x + source[opposite].x) / 2, bridge.sourceEndpoints[index].x) ||
      !close((source[corner].y + source[opposite].y) / 2, bridge.sourceEndpoints[index].y)
    )
      return empty();
  }
  const tangent = groundVector(bridge.sourceEndpoints[0], bridge.sourceEndpoints[1]);
  const normal = groundVector(source[0], source[3]);
  const groundLength = Math.hypot(tangent.x, tangent.y);
  if (
    !close(Math.hypot(normal.x, normal.y), bridge.groundWidthCells) ||
    !close(tangent.x * normal.x + tangent.y * normal.y, 0)
  )
    return empty();
  const dimensions = SETTLEMENT_BRIDGE_STRUCTURE;
  const span = groundLength - 2 * dimensions.endInsetCells;
  const intervals = Math.max(1, Math.ceil(span / dimensions.maxPostSpacingCells));
  if (!Number.isFinite(span) || span <= 0 || 2 * (intervals + 1) > dimensions.maxPosts)
    return empty();
  const height = dimensions.postHeight * scale;
  const railHeight = height * dimensions.railHeightRatio;
  const thickness = dimensions.railThickness * scale;
  const side = (a: IslandPoint, b: IslandPoint): SettlementBridgeSide => {
    const posts: SettlementBridgePost[] = [];
    for (let index = 0; index <= intervals; index++) {
      const fraction = (dimensions.endInsetCells + (span * index) / intervals) / groundLength;
      const base = { x: a.x + (b.x - a.x) * fraction, y: a.y + (b.y - a.y) * fraction };
      posts.push({ base, top: { x: base.x, y: base.y - height }, height, scale });
    }
    const rails = posts.slice(1).map((post, index): SettlementBridgeRail => {
      const previous = posts[index]!;
      const start = { x: previous.base.x, y: previous.base.y - railHeight };
      const end = { x: post.base.x, y: post.base.y - railHeight };
      return {
        quad: [
          start,
          end,
          { x: end.x, y: end.y + thickness },
          { x: start.x, y: start.y + thickness },
        ],
      };
    });
    posts.sort((a, b) => a.base.y - b.base.y || a.base.x - b.base.x);
    return { posts, rails };
  };
  const first = side(target[0], target[1]);
  const second = side(target[3], target[2]);
  if (
    ![first, second].every(
      ({ posts, rails }) =>
        posts.every(({ base, top }) => finitePoint(base) && finitePoint(top) && top.y < base.y) &&
        rails.every(({ quad }) => affineQuad(quad)),
    )
  )
    return empty();
  return target[0].y + target[1].y <= target[3].y + target[2].y
    ? { far: first, near: second }
    : { far: second, near: first };
}
